import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './MyAlpha.css';

const deployments = [
  {
    name: 'MyAlpha Rack',
    desc: 'Fully integrated rack appliance. We size compute for your concurrent users and model mix, ship, rack, and harden on your network. Best when you want a known quantity — power, cooling, support — without assembling a GPU stack yourself.',
  },
  {
    name: 'MyAlpha on NVIDIA',
    desc: 'Same MyAlpha runtime and industry packs, loaded onto your DGX or qualified NVIDIA workstation/server. Best when you already own the silicon and want the software, models, and support without another chassis.',
  },
];

const capabilities = [
  {
    title: 'Private by default',
    desc: 'Air-gappable. No required phone-home for day-to-day inference. Audit who asked what, on your terms.',
  },
  {
    title: 'Industry packs',
    desc: 'Domain prompts, retrieval schemas, and evaluation sets for your vertical — not a one-size chatbot skin.',
  },
  {
    title: 'Policy you control',
    desc: 'Connect your identity stack, set retention, and choose model behavior without a vendor’s content committee in the loop.',
  },
  {
    title: 'Ops-ready',
    desc: 'Monitoring, role-based access, backup of configs and indexes, documented runbooks for your IT team.',
  },
];

const notList = [
  'Not a public chatbot.',
  'Not a multi-tenant SaaS brain.',
  'Not a substitute for your compliance program — it’s the infrastructure that lets that program run on AI you actually control.',
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  },
};

export const MyAlpha = () => {
  return (
    <motion.div
      className="myalpha-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <section className="myalpha-hero">
        <div className="container">
          <motion.div className="myalpha-hero-content" variants={itemVariants}>
            <p className="myalpha-eyebrow">MyAlpha by RunDuckRun</p>
            <h1>
              MyAlpha — the on-prem LLM that works like your facility: contained,
              accountable, always on.
            </h1>
            <p className="lead">
              Turnkey rack appliance or software stack on your NVIDIA systems. Same
              product: private inference, industry context, customer-defined policy.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="deploy-section">
        <div className="container">
          <motion.h2
            className="section-label"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Two deployment paths
          </motion.h2>

          <motion.div
            className="deploy-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {deployments.map((item) => (
              <motion.div key={item.name} className="deploy-block" variants={itemVariants}>
                <h3>{item.name}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="capabilities-section">
        <div className="container">
          <motion.h2
            className="section-label"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            What MyAlpha delivers
          </motion.h2>

          <motion.div
            className="capabilities-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {capabilities.map((cap) => (
              <motion.div key={cap.title} className="capability" variants={itemVariants}>
                <h3>{cap.title}</h3>
                <p>{cap.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="policy-section">
        <div className="container">
          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Policy without politics</h2>
            <p>
              Public cloud assistants apply broad content filters that often block
              legitimate industry work — legal analysis, safety reporting, clinical edge
              cases, competitive research. MyAlpha is different: you choose the model
              family, you define acceptable use, and you enforce it with your own
              governance. We don’t inject ideological filters into your stack.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="not-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>What MyAlpha is not</h2>
            <ul className="not-list">
              {notList.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="myalpha-cta-section">
        <div className="container">
          <motion.div
            className="myalpha-cta"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Size MyAlpha for your facility</h2>
            <p>
              We’ll map concurrent users, model mix, and whether MyAlpha Rack or your
              NVIDIA box is the better fit.
            </p>
            <Link to="/contact?source=myalpha" className="btn btn-primary btn-lg">
              Schedule a MyAlpha sizing call
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default MyAlpha;
